/**
 * Shared rules for exercise name/type and numeric fields — used by OP EMR forms
 * and the doctor sidebar Exercise library (keep in sync).
 */
export const EXERCISE_NAME_TYPE_PATTERN = /^[A-Za-z0-9 _.,'-]+$/;

export const EXERCISE_NAME_PATTERN_MESSAGE =
  "Only letters, numbers, spaces, and basic symbols (.,'-_) are allowed";

/** @returns {string|null} */
export function getExerciseNameValidationError(name) {
  if (!name?.trim()) return "Exercise name is required";
  if (!EXERCISE_NAME_TYPE_PATTERN.test(name.trim())) return EXERCISE_NAME_PATTERN_MESSAGE;
  return null;
}

/** Type is optional; if provided it must match the same pattern as EMR. @returns {string|null} */
export function getExerciseTypeValidationError(exerciseType) {
  if (exerciseType === undefined || exerciseType === null) return null;
  const s = String(exerciseType).trim();
  if (s === "") return null;
  if (!EXERCISE_NAME_TYPE_PATTERN.test(s)) return EXERCISE_NAME_PATTERN_MESSAGE;
  return null;
}

/**
 * @param {string} label
 * @param {unknown} value
 * @param {{ required?: boolean }} opts
 * @returns {string|null}
 */
export function getNonNegativeNumberError(label, value, { required = false } = {}) {
  if (value === undefined || value === null || value === "") {
    return required ? `${label} is required` : null;
  }
  const n = Number(value);
  if (Number.isNaN(n)) return `${label} must be a valid number`;
  if (n < 0) return `${label} cannot be negative`;
  return null;
}

/**
 * Doctor exercise library add/edit — align with EMR + prescribed exercise expectations.
 * @returns {string|null} first error message or null
 */
export function getDoctorExerciseFormError(values) {
  const nameErr = getExerciseNameValidationError(values?.name);
  if (nameErr) return nameErr;
  const typeErr = getExerciseTypeValidationError(values?.exercise_type);
  if (typeErr) return typeErr;
  const freqErr = getNonNegativeNumberError("Frequency", values?.frequency, {
    required: true,
  });
  if (freqErr) return freqErr;
  const intErr = getNonNegativeNumberError("Intensity", values?.intensity, {
    required: true,
  });
  if (intErr) return intErr;
  return null;
}

/** Ant Design Form rules — same constraints as `ExerciseFormFields` (EMR prescribe flow). */
export const opExerciseNameFormRules = [
  { required: true, message: "Exercise name is required" },
  { pattern: EXERCISE_NAME_TYPE_PATTERN, message: EXERCISE_NAME_PATTERN_MESSAGE },
  {
    validator: (_, value) =>
      value && String(value).trim().length > 0
        ? Promise.resolve()
        : Promise.reject("Exercise name cannot be empty or just spaces"),
  },
];

export const opExerciseTypeFormRules = [
  {
    pattern: EXERCISE_NAME_TYPE_PATTERN,
    message: EXERCISE_NAME_PATTERN_MESSAGE,
  },
];

export const opFrequencyFormRules = [
  {
    validator: (_, value) => {
      if (Number(value) < 0) {
        return Promise.reject("Frequency cannot be negative");
      }
      return Promise.resolve();
    },
  },
];

export const opIntensityFormRules = [
  {
    validator: (_, value) => {
      if (Number(value) < 0) {
        return Promise.reject("Intensity cannot be negative");
      }
      return Promise.resolve();
    },
  },
];

/** Doctor library: frequency & intensity required, non-negative (matches prescribed edit). */
export const doctorExerciseFrequencyRules = [
  { required: true, message: "Frequency is required" },
  ...opFrequencyFormRules,
];

export const doctorExerciseIntensityRules = [
  { required: true, message: "Intensity is required" },
  ...opIntensityFormRules,
];
